import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import Slide from "@material-ui/core/Slide";
import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import Typography from "@material-ui/core/Typography";
import BlockIcon from "@material-ui/icons/Block";
import DoneIcon from "@material-ui/icons/Done";
import GetAppIcon from "@material-ui/icons/GetApp";
import ScheduleIcon from "@material-ui/icons/Schedule";
import PropTypes from "prop-types";
import React, { Fragment, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import MessageService from "@services/Message";
import ScheduleService from "@services/Schedule";
import { formatUserDate, formatUserDatetime } from "@utils/datetime";
import { formatPhone } from "@utils/form";
import {
  DestinationTypeEnum,
  FileFormatEnum,
  contentTypeLabels,
  fileFormatLabels,
} from "@utils/message";
import { uniqueKey } from "@utils/react";
import { ScheduleStatusEnum } from "@utils/schedule";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const useStyles = makeStyles(() => ({
  dialog: {},
  formContent: {
    width: "100%",
    overflow: "hidden",
    boxSizing: "border-box",
    "@media all and (min-width: 768px)": {
      width: "592px",
    },
  },
  table: {},
  statusLabel: {
    display: "flex",
    alignItems: "center",
  },
  statusLabelIcon: {
    marginRight: "12px",
  },
  destinationFileLabel: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  destinationFileLabelName: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  link: {
    color: "#3f51b5!important",
    cursor: "pointer",
    textDecoration: "none!important",
  },
}));

function ScheduleDetails(props) {
  const classes = useStyles();
  const params = useParams();

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [downloadingDestinationFile, setDownloadingDestinationFile] =
    useState(false);
  const [schedule, setSchedule] = useState(null);
  const [destinationRows, setDestinationRows] = useState([]);
  const [destinationPage, setDestinationPage] = useState(0);
  const [destinationPerPage, setDestinationPerPage] = useState(5);

  const statusInfo = {
    [ScheduleStatusEnum.PENDING]: {
      label: "Pendente",
      icon: (
        <ScheduleIcon className={classes.statusLabelIcon} color="primary" />
      ),
      value: ScheduleStatusEnum.PENDING,
    },
    [ScheduleStatusEnum.CANCELLED]: {
      label: "Cancelado",
      icon: <BlockIcon className={classes.statusLabelIcon} color="secondary" />,
      value: ScheduleStatusEnum.CANCELLED,
    },
    [ScheduleStatusEnum.PROCESSED]: {
      label: "Processado",
      icon: (
        <DoneIcon
          className={classes.statusLabelIcon}
          style={{ color: "#4caf50" }}
        />
      ),
      value: ScheduleStatusEnum.PROCESSED,
    },
  };

  function loadSchedule() {
    setLoading(true);
    ScheduleService.find(params.id)
      .then((data) => {
        pushSchedule(data);
        setLoading(false);
      })
      .catch((e) => {
        console.error(e);
        toast.error("Erro ao carregar dados do agendamento!");
        setLoading(false);
      });
  }

  function pushSchedule(schedule) {
    setSchedule(schedule);
    if (schedule.destinationType === DestinationTypeEnum.LIST) {
      setDestinationRows(schedule.destination.addressList);
    }
  }

  function downloadDestinationFile(fileName) {
    setDownloadingDestinationFile(true);
    MessageService.downloadFile(fileName)
      .then(() => {
        toast.success("Arquivo baixado!");
        setDownloadingDestinationFile(false);
      })
      .catch((e) => {
        console.error(e);
        toast.error("Erro ao baixar arquivo!");
        setDownloadingDestinationFile(false);
      });
  }

  function renderStatusLabel(status) {
    const info = statusInfo[status];
    return (
      <span className={classes.statusLabel}>
        {info.icon}
        {info.label}
      </span>
    );
  }

  function renderContentTypeLabel(contentType) {
    return contentTypeLabels[contentType];
  }

  function renderValidityLabel(validity) {
    return `${validity} minutos`;
  }

  function renderDestinationFileTypeLabel(fileType) {
    return fileFormatLabels[fileType];
  }

  function renderDestinationFileLabel(fileName) {
    const filePath = fileName.split("/");
    const onlyFileName = filePath[filePath.length - 1];
    return (
      <div className={classes.destinationFileLabel}>
        <span className={classes.destinationFileLabelName}>{onlyFileName}</span>
        <IconButton
          color="primary"
          disabled={downloadingDestinationFile}
          onClick={() => downloadDestinationFile(fileName)}
        >
          <GetAppIcon fontSize="small" />
        </IconButton>
      </div>
    );
  }

  function isDestinationFile() {
    return schedule && schedule.destinationType === DestinationTypeEnum.FILE;
  }

  function isDestinationFileWithMessage() {
    return (
      schedule &&
      schedule.destinationType === DestinationTypeEnum.FILE &&
      schedule.destination.file.format ===
        FileFormatEnum.DESTINATION_WITH_MESSAGE
    );
  }

  function handleClose() {
    if (!loading) {
      if (props.onClose) {
        props.onClose();
      } else if (props.onClosed) {
        setOpen(false);
        setTimeout(() => props.onClosed(), 500);
      }
    }
  }

  useEffect(() => {
    setOpen(props.open);

    if (props.open) {
      loadSchedule();
    }
  }, [props.open]);

  return (
    <Dialog
      className={classes.dialog}
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={handleClose}
    >
      <DialogTitle>Detalhes de agendamento</DialogTitle>
      <DialogContent className={classes.formContent}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="subtitle2">ID:</Typography>
            <Typography variant="body1">
              {schedule ? schedule.requestId : "---"}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2">Data de Criação:</Typography>
            <Typography variant="body1">
              {schedule ? formatUserDate(schedule.creationDate) : "---"}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2">Data/Hora de Envio:</Typography>
            <Typography variant="body1">
              {schedule ? formatUserDatetime(schedule.deliveryDatetime) : "---"}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2">Tempo de Expiração:</Typography>
            <Typography variant="body1">
              {schedule
                ? renderValidityLabel(schedule.template.validity)
                : "---"}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2">Status de Envio:</Typography>
            <Typography variant="body1">
              {schedule ? renderStatusLabel(schedule.state) : "---"}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2">Número de Origem:</Typography>
            <Typography variant="body1">
              {schedule ? schedule.template.senderId : "---"}
            </Typography>
          </Grid>
          {schedule && schedule.destinationType === DestinationTypeEnum.LIST && (
            <Grid item xs={12} sm={12}>
              <TableContainer>
                <Table className={classes.table} size={"medium"}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Números de Destino:</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {destinationRows
                      .slice(
                        destinationPage * destinationPerPage,
                        destinationPage * destinationPerPage +
                          destinationPerPage
                      )
                      .map((destination) => (
                        <TableRow key={uniqueKey()}>
                          <TableCell>{formatPhone(destination)}</TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                labelRowsPerPage="Linhas por página"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to === -1 ? count : to} de ${
                    count !== -1 ? count : `mais do que ${to}`
                  }`
                }
                rowsPerPageOptions={[5, 8, 10]}
                component="div"
                count={destinationRows.length}
                rowsPerPage={destinationPerPage}
                page={destinationPage}
                onPageChange={(e, value) => {
                  setDestinationPage(value);
                }}
                onRowsPerPageChange={(e) => {
                  setDestinationPerPage(parseInt(e.target.value, 10));
                }}
              />
            </Grid>
          )}
          {schedule &&
            schedule.destinationType === DestinationTypeEnum.SAVED_LIST && (
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2">Grupo de Destino:</Typography>
                <Typography variant="body1">
                  {schedule ? (
                    <Link
                      className={classes.link}
                      to={`/grupos/${schedule.destination.listId}`}
                    >
                      {schedule.destination.listId}
                    </Link>
                  ) : (
                    "---"
                  )}
                </Typography>
              </Grid>
            )}
          {isDestinationFile() && (
            <Fragment>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2">Tipo de Arquivo:</Typography>
                <Typography variant="body1">
                  {renderDestinationFileTypeLabel(
                    schedule.destination.file.format
                  )}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2">Arquivo:</Typography>
                <Typography variant="body1">
                  {renderDestinationFileLabel(schedule.destination.file.name)}
                </Typography>
              </Grid>
            </Fragment>
          )}

          {isDestinationFileWithMessage() || (
            <Fragment>
              {/* Binary messages disabled.
              <Grid item xs={12} sm={3}>
                <Typography variant="subtitle2">Tipo de Conteúdo:</Typography>
                <Typography variant="body1">
                  {schedule
                    ? renderContentTypeLabel(schedule.template.messageType)
                    : "---"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={9}>
                <Typography variant="subtitle2">Mensagem:</Typography>
                <Typography variant="body1">
                  {schedule ? schedule.template.textMessage : "---"}
                </Typography>
              </Grid>
              */}
              <Grid item xs={12}>
                <Typography variant="subtitle2">Mensagem:</Typography>
                <Typography variant="body1">
                  {schedule ? schedule.template.textMessage : "---"}
                </Typography>
              </Grid>
            </Fragment>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ScheduleDetails.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  onClosed: PropTypes.func,
};

ScheduleDetails.defaultProps = {
  open: true,
  onClose: null,
  onClosed: null,
};

export default ScheduleDetails;
